import { asIsoDate, type BarSeries, type Drawing, type DrawingPayload } from '@stockstop/shared';
import {
  CandlestickSeries,
  createChart,
  CrosshairMode,
  LineStyle,
  type CandlestickData,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
  type Time,
} from 'lightweight-charts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChartCoordinates } from './drawings/coordinates.js';
import { DrawingLayer } from './drawings/layer.js';
import type { HitResult, Point } from './drawings/types.js';

/**
 * Chart lifecycle and pointer handling.
 *
 * The chart instance lives outside React state on purpose: lightweight-charts
 * manages its own canvas, and re-creating it on every render would discard the
 * user's zoom and pan. React owns *when* data flows in; the library owns the
 * pixels.
 *
 * Pointer handling is the delicate part. The chart and the drawing layer both want
 * drag gestures, so scroll and scale are switched off for the duration of a
 * drawing drag and restored afterwards — otherwise moving a line would pan the
 * chart underneath it.
 */

export interface UseChartOptions {
  series: BarSeries | undefined;
  drawings: Drawing[];
  /**
   * Der eingebuchte Stop, falls er von der Stop-Loss-Linie abweicht.
   *
   * Wird als Ghost-Linie gezeichnet — bewusst **keine** Zeichnung: Sie gehört
   * nicht dem Nutzer, sondern spiegelt die Order beim Broker. Als Zeichnung wäre
   * sie auswählbar und verschiebbar, und ein Zug daran versprächt, die Order zu
   * ändern, was er nicht kann.
   */
  ghostStop: number | null;
  currency: string | null;
  onGeometryCommitted: (drawingId: number, payload: DrawingPayload) => void;
  onSelectionChanged: (drawingId: number | null) => void;
  /** Die ausgewählte Zeichnung soll entfernt werden (Entf- oder Rücktaste). */
  onDeleteRequested: (drawingId: number) => void;
  /**
   * Doppelklick auf eine freie Stelle, mit dem Kurs an dieser Höhe.
   *
   * Der Handelstag wird nicht übergeben: Die einzige Zeichnung, die daraus
   * entsteht, ist die waagerechte Stop-Loss-Linie, für die allein der Kurs zählt.
   */
  onEmptyClick: (price: number) => void;
}

export interface UseChartResult {
  /**
   * An das Chart-Element hängen.
   *
   * Eine Callback-Ref, keine Objekt-Ref: React ruft sie auf, sobald das Element
   * eingehängt wird. Das ist entscheidend, weil der Container erst erscheint,
   * nachdem eine Position gewählt wurde — eine Objekt-Ref würde den Aufbau-Effekt
   * darüber nicht benachrichtigen.
   */
  containerRef: (node: HTMLDivElement | null) => void;
  /** The drawing layer, so the page can clear a draft once the save returned. */
  layerRef: React.RefObject<DrawingLayer | null>;
  /** True once the chart exists and has data. */
  isReady: boolean;
  /** Resets zoom to show the full history. */
  fitContent: () => void;
  /** Zooms to the most recent `days` trading days. */
  showLastDays: (days: number) => void;
}

export const useChart = ({
  series,
  drawings,
  ghostStop,
  currency,
  onGeometryCommitted,
  onSelectionChanged,
  onDeleteRequested,
  onEmptyClick,
}: UseChartOptions): UseChartResult => {
  /**
   * Der Chart-Container, sobald React ihn eingehängt hat.
   *
   * Bewusst als State und nicht als Ref: Der Container wird erst gerendert, wenn
   * eine Position ausgewählt ist. Beim ersten Durchlauf existiert er nicht, und
   * eine Ref würde stillschweigend `null` bleiben — der Aufbau-Effekt liefe ins
   * Leere und, mit leerer Abhängigkeitsliste, nie wieder. Genau daran blieb der
   * Chart dauerhaft leer, ohne dass ein Fehler auftrat.
   */
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    setContainer(node);
  }, []);

  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const coordinatesRef = useRef<ChartCoordinates | null>(null);
  const layerRef = useRef<DrawingLayer | null>(null);
  const ghostRef = useRef<IPriceLine | null>(null);
  /**
   * Zählt, wie oft die Chart-Instanz aufgebaut wurde.
   *
   * Die Effekte, die Kerzen und Zeichnungen hineinschreiben, hängen an den Daten.
   * Wird der Chart neu aufgebaut, ohne dass sich die Daten ändern, liefe keiner
   * von ihnen erneut und die frische Instanz bliebe leer. Unter `StrictMode`
   * greift das noch nicht, weil React dort ohnehin sämtliche Effekte wiederholt —
   * sobald der Aufbau-Effekt aber je eine Abhängigkeit bekommt (etwa einen
   * Themenwechsel), wäre genau das der Fall. Der Marker hält die beiden Effekte
   * gekoppelt, bevor das passieren kann.
   */
  const [chartGeneration, setChartGeneration] = useState(0);

  /* Callbacks are read through a ref so the chart is never rebuilt when a parent
   * re-render produces new function identities. */
  const callbacksRef = useRef({
    onGeometryCommitted,
    onSelectionChanged,
    onDeleteRequested,
    onEmptyClick,
  });
  callbacksRef.current = {
    onGeometryCommitted,
    onSelectionChanged,
    onDeleteRequested,
    onEmptyClick,
  };

  /* ------------------------------------------------------------ construction */

  useEffect(() => {
    if (!container) return;

    const chart = createChart(container, {
      autoSize: true,
      layout: {
        background: { color: 'transparent' },
        textColor: '#4b5563',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
      },
      grid: {
        vertLines: { color: 'rgba(148, 163, 184, 0.18)' },
        horzLines: { color: 'rgba(148, 163, 184, 0.18)' },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: 'rgba(148, 163, 184, 0.4)' },
      timeScale: {
        borderColor: 'rgba(148, 163, 184, 0.4)',
        /* Room on the right so a trend line extended into the future is visible. */
        rightOffset: 12,
        timeVisible: false,
      },
      handleScroll: { mouseWheel: true, pressedMouseMove: true },
      handleScale: { mouseWheel: true, pinch: true, axisPressedMouseMove: true },
    });

    const candles = chart.addSeries(CandlestickSeries, {
      upColor: '#16a34a',
      downColor: '#dc2626',
      borderUpColor: '#16a34a',
      borderDownColor: '#dc2626',
      wickUpColor: '#16a34a',
      wickDownColor: '#dc2626',
    });

    const coordinates = new ChartCoordinates(chart, candles);
    const layer = new DrawingLayer(coordinates, {
      onGeometryCommitted: (id, payload) => callbacksRef.current.onGeometryCommitted(id, payload),
      onSelectionChanged: (id) => callbacksRef.current.onSelectionChanged(id),
      onDeleteRequested: (id) => callbacksRef.current.onDeleteRequested(id),
      onCursorChanged: (cursor) => {
        container.style.cursor = cursor;
      },
    });
    candles.attachPrimitive(layer);

    chartRef.current = chart;
    seriesRef.current = candles;
    coordinatesRef.current = coordinates;
    layerRef.current = layer;
    /* Meldet den Daten-Effekten, dass sie in diese Instanz schreiben müssen. */
    setChartGeneration((generation) => generation + 1);

    /* ---------------------------------------------------------- interaction */

    const toPoint = (event: MouseEvent): Point => {
      const rect = container.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };

    /** Freezes chart panning so a drawing drag does not also move the chart. */
    const setChartInteractive = (enabled: boolean): void => {
      chart.applyOptions({
        handleScroll: { mouseWheel: enabled, pressedMouseMove: enabled },
        handleScale: { mouseWheel: enabled, pinch: enabled, axisPressedMouseMove: enabled },
      });
    };

    let pendingHit: HitResult | null = null;

    const onMouseDown = (event: MouseEvent): void => {
      if (event.button !== 0) return;
      const point = toPoint(event);
      const hit = layer.findAt(point, container.clientWidth, container.clientHeight);

      if (hit) {
        pendingHit = hit;
        setChartInteractive(false);
        layer.beginDrag(hit, point);
        event.preventDefault();
      } else {
        layer.setSelected(null);
      }
    };

    const onMouseMove = (event: MouseEvent): void => {
      const point = toPoint(event);

      if (layer.isDragging) {
        layer.updateDrag(point);
        event.preventDefault();
        return;
      }

      /* Nicht am Ziehen: melden, worüber der Zeiger steht. */
      const hit = layer.findAt(point, container.clientWidth, container.clientHeight);
      container.style.cursor = hit ? hit.cursor : 'default';
      layer.setHovered(hit?.drawingId ?? null);
    };

    /* Verlässt der Zeiger den Chart, verschwinden auch die Beschriftungen. */
    const onMouseLeave = (): void => {
      if (!layer.isDragging) layer.setHovered(null);
    };

    const onMouseUp = (): void => {
      if (layer.isDragging) {
        layer.endDrag();
        setChartInteractive(true);
      } else if (pendingHit === null) {
        /* A click on empty space: let the page place a new drawing there. */
      }
      pendingHit = null;
    };

    const onDoubleClick = (event: MouseEvent): void => {
      const point = toPoint(event);
      const hit = layer.findAt(point, container.clientWidth, container.clientHeight);
      if (hit) return;

      const price = coordinatesRef.current?.priceForY(point.y);
      if (price !== null && price !== undefined && price > 0) {
        callbacksRef.current.onEmptyClick(price);
      }
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      /* Escape bricht ein laufendes Ziehen ab und stellt die Geometrie wieder her. */
      if (event.key === 'Escape' && layer.isDragging) {
        layer.cancelDrag();
        setChartInteractive(true);
        return;
      }

      /*
       * Entfernen der ausgewählten Zeichnung. Nur wenn der Fokus nicht in einem
       * Eingabefeld steht — sonst löschte die Rücktaste beim Umbenennen die
       * Zeichnung statt eines Buchstabens.
       */
      if (event.key === 'Delete' || event.key === 'Backspace') {
        const target = event.target as HTMLElement | null;
        const isTextInput =
          target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement ||
          target?.isContentEditable === true;
        if (isTextInput || layer.isDragging) return;

        if (layer.requestDeleteSelected()) event.preventDefault();
      }
    };

    container.addEventListener('mousedown', onMouseDown);
    container.addEventListener('dblclick', onDoubleClick);
    container.addEventListener('mouseleave', onMouseLeave);
    /* Move and up are bound to the window so a drag survives the pointer
     * leaving the chart area. */
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('keydown', onKeyDown);

    return () => {
      container.removeEventListener('mousedown', onMouseDown);
      container.removeEventListener('dblclick', onDoubleClick);
      container.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('keydown', onKeyDown);

      candles.detachPrimitive(layer);
      /* Die Preislinie gehört der Serie; mit ihr fällt sie ohnehin weg, aber die
       * Referenz darf nicht auf eine abgebaute Instanz zeigen. */
      ghostRef.current = null;
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      coordinatesRef.current = null;
      layerRef.current = null;
    };
  }, [container]);

  /* -------------------------------------------------------------------- data */

  useEffect(() => {
    const candles = seriesRef.current;
    const coordinates = coordinatesRef.current;
    const layer = layerRef.current;
    if (!candles || !coordinates || !layer || !series) return;

    const data: CandlestickData<Time>[] = series.bars.map((bar) => ({
      time: bar.date,
      open: bar.open,
      high: bar.high,
      low: bar.low,
      close: bar.close,
    }));

    candles.setData(data);
    coordinates.setDates(series.bars.map((bar) => bar.date));

    const latest = series.bars.at(-1);
    layer.setMarketContext(latest ? asIsoDate(latest.date) : null, latest?.close ?? null, currency);

    /* Open on a readable window rather than 20 years squeezed into the width. */
    chartRef.current?.timeScale().setVisibleLogicalRange({
      from: Math.max(0, data.length - 250),
      to: data.length + 12,
    });
  }, [series, currency, chartGeneration]);

  useEffect(() => {
    layerRef.current?.setDrawings(drawings);
  }, [drawings, chartGeneration]);

  /*
   * Die Ghost-Linie des eingebuchten Stops.
   *
   * Als `createPriceLine` der Serie und nicht über die Zeichenebene: Damit bekommt
   * sie von selbst eine Marke an der Kursachse, bleibt beim Zoomen an ihrem Kurs
   * und ist — der eigentliche Grund — nicht anfassbar. Sie zeigt, was beim Broker
   * liegt; daran lässt sich von hier aus nichts ziehen.
   */
  useEffect(() => {
    const candles = seriesRef.current;
    if (!candles) return;

    if (ghostRef.current) {
      candles.removePriceLine(ghostRef.current);
      ghostRef.current = null;
    }

    if (ghostStop == null) return;

    ghostRef.current = candles.createPriceLine({
      price: ghostStop,
      /* Gedämpftes Blau: dieselbe Familie wie die Stop-Loss-Linie, damit die
       * Zugehörigkeit erkennbar ist, aber sichtbar zurückgenommen. */
      color: 'rgba(41, 98, 255, 0.45)',
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      axisLabelVisible: true,
      title: 'eingebucht',
    });

    return () => {
      if (candles && ghostRef.current) {
        candles.removePriceLine(ghostRef.current);
        ghostRef.current = null;
      }
    };
  }, [ghostStop, chartGeneration]);

  /* ----------------------------------------------------------------- actions */

  const fitContent = useCallback(() => {
    chartRef.current?.timeScale().fitContent();
  }, []);

  const showLastDays = useCallback((days: number) => {
    const count = coordinatesRef.current?.barCount ?? 0;
    if (count === 0) return;
    chartRef.current?.timeScale().setVisibleLogicalRange({
      from: Math.max(0, count - days),
      to: count + 12,
    });
  }, []);

  return { containerRef, layerRef, isReady: chartGeneration > 0, fitContent, showLastDays };
};
